import omit from "lodash/omit";
import some from "lodash/some";
import {
  FIELD_ASSIGNEE_ID_KEY,
  FIELD_ATTACHMENT_IDS_KEY,
  FIELD_AUDIENCES_KEY,
  FIELD_BOOKING_CATEGORY_ID_KEY,
  FIELD_COMMUNICATION_TYPE_KEY,
  FIELD_CUSTOM_FIELDS_KEY,
  FIELD_DESCRIPTION_KEY,
  FIELD_DEVELOPMENTAL_KEY,
  FIELD_ID_KEY,
  FIELD_INTEREST_QUESTIONS_KEY,
  FIELD_INTEREST_STATEMENT_ENABLED_KEY,
  FIELD_MARKED_FOR_REMOVAL_KEY,
  FIELD_METADATA_KEY,
  FIELD_MIN_REQUIRED_COUNT_KEY,
  FIELD_NAME_KEY,
  FIELD_PARENT_ACTIVITY_ID_KEY,
  FIELD_POST_UNTIL_CHANGE_REASON_KEY,
  FIELD_POST_UNTIL_KEY,
  FIELD_PRIVACY_KEY,
  FIELD_SKILLS_FRAMEWORK_IDS_KEY,
  FIELD_STATE_KEY,
  FIELD_SUBTEMPLATE_KEY,
  FIELD_TEMPLATE_ID_KEY,
  FIELD_UNIQUE_ID_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { TemplateKey } from "PFApp/constants/templates";
import { convertAPICustomFieldsToFormValues } from "PFApp/helpers/activity_form/data_conversions";
import { getPrivacyConfiguration } from "PFApp/helpers/activity_form/get_privacy_configuration";
import { getPostUntilChangeReason } from "PFApp/helpers/activity_form/post_until_change_reason";
import { useInterestQuestionariesTemplateConfiguration, useTemplate } from "PFApp/hooks";
import { useInitialQuestionsValues } from "PFApp/hooks/activity_form/use_initial_questions_values";
import { useMultiActivityFormStore } from "PFApp/stores/activity_form/multi_roles_form";
import { ActivityFormType } from "PFApp/types/activity_form/activity_form_schema";
import { isProfileActive } from "PFCore/helpers/profile";
import { useSubtemplates } from "PFCore/hooks/queries/subtemplates/use_subtemplates";
import { Activity, AvailabilityMode } from "PFCore/types";

import { useActivityFormDefaultAttributes } from "../activity_form_default_attributes/use_activity_form_default_attributes";

type UseInitialRoleDataParams = {
  fieldsBlacklist?: string[];
};

type GetInitialRoleDataParams = {
  uniqueId: string;
  subtemplateKey: string;
  isNew: boolean;
  activity?: Activity;
  forceInterestQuestionsFromActivity?: boolean;
};

export const useInitialRoleData = ({ fieldsBlacklist = [] }: UseInitialRoleDataParams) => {
  const parentActivity = useMultiActivityFormStore((store) => store.parentActivity);
  const roleTemplate = useTemplate(TemplateKey.Role)!;
  const templateConfig = useInterestQuestionariesTemplateConfiguration(roleTemplate);
  const { getInitialQuestionsValues } = useInitialQuestionsValues({ template: roleTemplate });
  const { getActivityFormDefaultAttributes } = useActivityFormDefaultAttributes({
    template: roleTemplate,
    parentActivity
  });
  const { data: subtemplates = [] } = useSubtemplates({ templateKey: TemplateKey.Role });

  const getInitialRoleData = ({
    uniqueId,
    subtemplateKey,
    isNew,
    activity,
    forceInterestQuestionsFromActivity
  }: GetInitialRoleDataParams): ActivityFormType => {
    const { isDefaultPrivate } = getPrivacyConfiguration({
      template: roleTemplate,
      parentActivity
    });
    const { initialQuestionsValues } = getInitialQuestionsValues({
      inheritFromActivity: !isNew || !!forceInterestQuestionsFromActivity,
      isClone: false,
      activity: activity || ({} as Activity)
    });
    const defaultAvailabilityThreshold = roleTemplate.schema.properties?.availability?.default_threshold;
    const defaultAttributes = isNew
      ? getActivityFormDefaultAttributes({
          isClone: false,
          attributes: activity || {},
          activityCreator: null,
          subtemplate: subtemplates.find(({ subtemplate_key }) => subtemplate_key === subtemplateKey) ?? null
        })
      : activity || {};

    return omit(
      {
        [FIELD_UNIQUE_ID_KEY]: uniqueId,
        ...(isNew ? {} : { [FIELD_ID_KEY]: defaultAttributes.id }),
        [FIELD_STATE_KEY]: "new",
        [FIELD_TEMPLATE_ID_KEY]: roleTemplate.id,
        [FIELD_SUBTEMPLATE_KEY]: subtemplateKey,
        [FIELD_NAME_KEY]: defaultAttributes.name || "",
        [FIELD_DESCRIPTION_KEY]: defaultAttributes.description || "",
        [FIELD_ASSIGNEE_ID_KEY]: isProfileActive(defaultAttributes.assignee?.profile)
          ? defaultAttributes?.assignee?.profile?.id || null
          : null,
        [FIELD_PARENT_ACTIVITY_ID_KEY]: parentActivity?.id ?? null,
        [FIELD_MIN_REQUIRED_COUNT_KEY]: defaultAttributes.min_required_count || 1,
        [FIELD_ATTACHMENT_IDS_KEY]: [], // TODO: [SP-7181] How to handle initial attachments data?
        [FIELD_INTEREST_STATEMENT_ENABLED_KEY]: !!templateConfig?.showStatement,
        [FIELD_INTEREST_QUESTIONS_KEY]: !some(initialQuestionsValues || [], Boolean)
          ? null
          : initialQuestionsValues,
        [FIELD_PRIVACY_KEY]: !!defaultAttributes.private || isDefaultPrivate,
        [FIELD_AUDIENCES_KEY]: defaultAttributes.audiences || [],
        [FIELD_COMMUNICATION_TYPE_KEY]: defaultAttributes.communication_type || "direct",
        [FIELD_CUSTOM_FIELDS_KEY]: convertAPICustomFieldsToFormValues(
          (defaultAttributes as Activity).custom_fields || []
        ),
        [FIELD_POST_UNTIL_KEY]: defaultAttributes.post_until || "",
        [FIELD_POST_UNTIL_CHANGE_REASON_KEY]: getPostUntilChangeReason(
          defaultAttributes.post_until_change_reason ?? null,
          roleTemplate.localized_post_until_change_reasons ?? []
        ),
        [FIELD_DEVELOPMENTAL_KEY]: defaultAttributes.developmental || false,
        [FIELD_SKILLS_FRAMEWORK_IDS_KEY]: defaultAttributes.skills_framework_ids || [],
        [FIELD_BOOKING_CATEGORY_ID_KEY]: defaultAttributes.booking_category_id ?? undefined,
        [FIELD_METADATA_KEY]: {
          ...omit(defaultAttributes.metadata, [
            "interest_questions",
            "interest_statement_enabled",
            "booking_category_id"
          ]),
          availability: defaultAttributes.metadata?.availability || {
            availability_threshold: defaultAvailabilityThreshold ?? 70,
            mode: AvailabilityMode.PerWeek, // The mode is hardcoded for MVP
            ranges: []
          }
        },
        [FIELD_MARKED_FOR_REMOVAL_KEY]: false
      },
      ...fieldsBlacklist
    ) as ActivityFormType;
  };

  return { getInitialRoleData };
};
